import React, { useState, useEffect } from 'react';
import { isAssembleeGenerale, getReunionTypeShort, formatReunionElectionLabel } from '../utils/reunionTypes';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganisme } from '../context/OrganismeContext';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

export default function ModifierCompositionADAR() {
  const navigate = useNavigate();
  const { organismeActif } = useOrganisme();
  
  // États pour les données
  const [composition, setComposition] = useState({ actifs: [], inactifs: [] });
  const [adherents, setAdherents] = useState([]);
  const [reunions, setReunions] = useState([]);
  const [, setFractions] = useState([]);
  const [fonctions, setFonctions] = useState([]);
  const [periodesEnCours, setPeriodesEnCours] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // États pour les modifications par section
  const [modificationsBureau, setModificationsBureau] = useState({ ajouts: [], retraits: [], modifications: [] });
  const [modificationsConseil, setModificationsConseil] = useState({ ajouts: [], retraits: [], modifications: [] });
  const [modificationsSecondCollege, setModificationsSecondCollege] = useState({ ajouts: [], retraits: [], modifications: [] });
  const [modificationsStagiaires, setModificationsStagiaires] = useState({ ajouts: [], retraits: [], modifications: [] });
  
  // États pour les modals
  const [showAjoutBureauModal, setShowAjoutBureauModal] = useState(false);
  const [showAjoutConseilModal, setShowAjoutConseilModal] = useState(false);
  const [showAjoutSecondCollegeModal, setShowAjoutSecondCollegeModal] = useState(false);
  const [showAjoutStagiaireModal, setShowAjoutStagiaireModal] = useState(false);
  const [showRetraitModal, setShowRetraitModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // États pour la recherche d'adhérents
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  
  // États pour les formulaires
  const [ajoutForm, setAjoutForm] = useState({
    type: '', // 'bureau', 'conseil', 'second_college', 'stagiaire'
    fonction: '',
    fraction: '',
    ordre: null,
    reunionElection: '',
    estMembreDeDroit: false,
    estMembreDeDroitSecondCollege: false,
    typeMembreDeDroit: '',
    numeroPosteSecondCollege: null,
    labelPosteSecondCollege: '',
    structureSecondCollege: '',
    nomRepresentantSecondCollege: '',
    emailRepresentantSecondCollege: '',
  });
  
  const [retraitForm, setRetraitForm] = useState({
    membreId: null,
    raisonRetrait: '',
    raisonRetraitAutre: '',
    dateRetrait: new Date().toISOString().split('T')[0],
  });
  
  const [generatePdf, setGeneratePdf] = useState(false);
  const [activeSection, setActiveSection] = useState('conseil');

  // Chargement des données
  useEffect(() => {
    if (organismeActif !== 'ADAR') {
      navigate('/admin/composition-conseil');
      return;
    }
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organismeActif]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const [compositionRes, adherentsRes, reunionsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}?includeInactifs=true`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/eligibles`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/reunions`, { headers })
      ]);

      setComposition({
        actifs: compositionRes.data.actifs,
        inactifs: compositionRes.data.inactifs,
      });
      setFractions(compositionRes.data.fractions);
      setFonctions(compositionRes.data.fonctions);
      setPeriodesEnCours(compositionRes.data.periodesEnCours || {});
      setAdherents(adherentsRes.data);
      setReunions(reunionsRes.data.filter(r => isAssembleeGenerale(r.type) || r.type === 'conseil_syndical'));
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les membres par type
  const membresBureau = composition.actifs.filter(m => m.type === 'bureau');
  const membresConseil = composition.actifs.filter(m => m.type === 'conseil');
  const membresSecondCollege = composition.actifs.filter(m => m.type === 'second_college');
  const membresStagiaires = composition.actifs.filter(m => m.type === 'stagiaire');

  // Fonctions utilitaires
  const getFonctionLabel = (value) => {
    return fonctions.find(f => f.value === value)?.label || value;
  };

  /** Recherche fiable d'une réunion (le select renvoie une string, Mongo renvoie souvent un ObjectId). */
  const getReunionById = (reunionId) => {
    if (reunionId == null) return null;
    return reunions.find((r) => String(r._id) === String(reunionId)) || null;
  };

  const getReunionLabel = (reunionId) => {
    const reunion = getReunionById(reunionId);
    if (!reunion) return '';
    return `${getReunionTypeShort(reunion.type, 'ADAR')} ${new Date(reunion.date).toLocaleDateString('fr-FR')}`;
  };

  const getPeriodeLabel = (fraction) => {
    const periode = periodesEnCours[fraction];
    if (!periode) return '-';
    return `${periode.debut}-${periode.fin}`;
  };

  const getSecondCollegeDisplay = (m) => {
    if (m?.secondCollegeHorsPlateforme) {
      return {
        line1: m.nomRepresentantSecondCollege || '—',
        line2: m.structureSecondCollege ? `(${m.structureSecondCollege})` : '',
        email: m.emailRepresentantSecondCollege,
      };
    }
    return {
      line1: m?.adherent ? `${m.adherent.prenom} ${m.adherent.nom?.toUpperCase?.() || ''}`.trim() : '—',
      line2: '',
      email: m?.adherent?.email,
    };
  };

  // Filtrer les adhérents pour la recherche selon le type de modal ouvert
  const getFilteredAdherents = () => {
    if (!searchTerm) return [];
    
    const baseFiltered = adherents.filter(adherent => {
      const fullName = `${adherent.prenom} ${adherent.nom}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    });

    // Déterminer le type de corps actuel selon le modal ouvert
    let typeCorps = null;
    if (showAjoutBureauModal) typeCorps = 'bureau';
    else if (showAjoutConseilModal) typeCorps = 'conseil';
    else if (showAjoutSecondCollegeModal) typeCorps = 'second_college';
    else if (showAjoutStagiaireModal) typeCorps = 'stagiaire';

    if (!typeCorps) return baseFiltered.slice(0, 10);

    // Filtrer les adhérents déjà présents dans ce corps (sauf s'ils sont en retrait)
    return baseFiltered.filter(adherent => {
      // Vérifier si l'adhérent est déjà dans ce corps
      const dejaPresent = composition.actifs.find(m => 
        m.type === typeCorps && 
        m.adherent?._id === adherent._id
      );

      if (!dejaPresent) return true; // Pas présent, on peut l'ajouter

      // Si présent, vérifier s'il est marqué pour retrait
      let retraits = [];
      if (typeCorps === 'bureau') retraits = modificationsBureau.retraits;
      else if (typeCorps === 'conseil') retraits = modificationsConseil.retraits;
      else if (typeCorps === 'second_college') retraits = modificationsSecondCollege.retraits;
      else if (typeCorps === 'stagiaire') retraits = modificationsStagiaires.retraits;

      const enRetrait = retraits.some(r => r.membreId === dejaPresent._id);
      return enRetrait; // Autoriser seulement si en retrait
    }).slice(0, 10);
  };

  const filteredAdherents = getFilteredAdherents();

  // Gestion de l'ajout au bureau
  const handleOpenAjoutBureau = (fonction) => {
    setAjoutForm({
      type: 'bureau',
      fonction,
      fraction: '',
      ordre: null,
      reunionElection: '',
      estMembreDeDroit: false,
      estMembreDeDroitSecondCollege: false,
      typeMembreDeDroit: '',
      numeroPosteSecondCollege: null,
      labelPosteSecondCollege: '',
    });
    setSelectedAdherent(null);
    setSearchTerm('');
    setShowAjoutBureauModal(true);
  };

  const handleConfirmAjoutBureau = () => {
    if (!selectedAdherent || !ajoutForm.reunionElection) return;

    const reunion = getReunionById(ajoutForm.reunionElection);
    if (!reunion) {
      alert('Réunion d’élection introuvable. Rechargez la page ou choisissez une autre réunion.');
      return;
    }

    setModificationsBureau({
      ...modificationsBureau,
      ajouts: [...modificationsBureau.ajouts, {
        adherent: selectedAdherent,
        type: 'bureau',
        fonction: ajoutForm.fonction,
        reunionElection: reunion,
      }]
    });

    setShowAjoutBureauModal(false);
    setSelectedAdherent(null);
    setSearchTerm('');
  };

  // Gestion de l'ajout au conseil
  const handleOpenAjoutConseil = (fraction, ordre, estMembreDeDroit = false) => {
    setAjoutForm({
      type: 'conseil',
      fonction: '',
      fraction: estMembreDeDroit ? '' : fraction,
      ordre: estMembreDeDroit ? null : ordre,
      reunionElection: '',
      estMembreDeDroit,
      estMembreDeDroitSecondCollege: false,
      typeMembreDeDroit: '',
      numeroPosteSecondCollege: null,
    });
    setSelectedAdherent(null);
    setSearchTerm('');
    setShowAjoutConseilModal(true);
  };

  const handleConfirmAjoutConseil = () => {
    if (!selectedAdherent || !ajoutForm.reunionElection) return;

    const reunion = getReunionById(ajoutForm.reunionElection);
    if (!reunion) {
      alert('Réunion d’élection introuvable. Rechargez la page ou choisissez une autre réunion.');
      return;
    }

    setModificationsConseil({
      ...modificationsConseil,
      ajouts: [...modificationsConseil.ajouts, {
        adherent: selectedAdherent,
        type: 'conseil',
        fraction: ajoutForm.fraction,
        ordre: ajoutForm.ordre,
        reunionElection: reunion,
        estMembreDeDroit: ajoutForm.estMembreDeDroit,
      }]
    });

    setShowAjoutConseilModal(false);
    setSelectedAdherent(null);
    setSearchTerm('');
  };

  // Gestion de l'ajout au second collège (représentants externes, sans compte adhérent)
  const handleOpenAjoutSecondCollege = (typeMembreDeDroit = null, numeroPoste = null, labelPoste = '') => {
    setAjoutForm({
      type: 'second_college',
      fonction: '',
      fraction: '',
      ordre: null,
      reunionElection: '',
      estMembreDeDroit: false,
      estMembreDeDroitSecondCollege: !!typeMembreDeDroit,
      typeMembreDeDroit: typeMembreDeDroit || '',
      numeroPosteSecondCollege: numeroPoste,
      labelPosteSecondCollege: labelPoste,
      structureSecondCollege: '',
      nomRepresentantSecondCollege: '',
      emailRepresentantSecondCollege: '',
    });
    setSelectedAdherent(null);
    setSearchTerm('');
    setShowAjoutSecondCollegeModal(true);
  };

  const handleConfirmAjoutSecondCollege = () => {
    if (!ajoutForm.reunionElection) return;
    if (
      !ajoutForm.structureSecondCollege?.trim() ||
      !ajoutForm.nomRepresentantSecondCollege?.trim() ||
      !ajoutForm.emailRepresentantSecondCollege?.trim()
    ) {
      alert('Renseignez la structure, le représentant et son e-mail.');
      return;
    }
    if (!ajoutForm.estMembreDeDroitSecondCollege && !ajoutForm.labelPosteSecondCollege?.trim()) {
      alert('Renseignez le libellé du poste (postes 1 à 3).');
      return;
    }

    const reunion = getReunionById(ajoutForm.reunionElection);
    if (!reunion) {
      alert('Réunion d’élection introuvable. Rechargez la page ou choisissez une autre réunion.');
      return;
    }

    setModificationsSecondCollege({
      ...modificationsSecondCollege,
      ajouts: [
        ...modificationsSecondCollege.ajouts,
        {
          type: 'second_college',
          secondCollegeHorsPlateforme: true,
          reunionElection: reunion,
          estMembreDeDroitSecondCollege: ajoutForm.estMembreDeDroitSecondCollege,
          typeMembreDeDroit: ajoutForm.typeMembreDeDroit,
          numeroPosteSecondCollege: ajoutForm.numeroPosteSecondCollege,
          labelPosteSecondCollege: ajoutForm.estMembreDeDroitSecondCollege
            ? ''
            : ajoutForm.labelPosteSecondCollege,
          structureSecondCollege: ajoutForm.structureSecondCollege.trim(),
          nomRepresentantSecondCollege: ajoutForm.nomRepresentantSecondCollege.trim(),
          emailRepresentantSecondCollege: ajoutForm.emailRepresentantSecondCollege.trim(),
        },
      ],
    });

    setShowAjoutSecondCollegeModal(false);
    setSearchTerm('');
  };

  // Gestion de l'ajout de stagiaire
  const handleOpenAjoutStagiaire = () => {
    setAjoutForm({
      type: 'stagiaire',
      fonction: '',
      fraction: '',
      ordre: null,
      reunionElection: '',
      estMembreDeDroit: false,
      estMembreDeDroitSecondCollege: false,
      typeMembreDeDroit: '',
      numeroPosteSecondCollege: null,
      labelPosteSecondCollege: '',
    });
    setSelectedAdherent(null);
    setSearchTerm('');
    setShowAjoutStagiaireModal(true);
  };

  const handleConfirmAjoutStagiaire = () => {
    if (!selectedAdherent || !ajoutForm.reunionElection) return;

    const reunion = getReunionById(ajoutForm.reunionElection);
    if (!reunion) {
      alert('Réunion d’élection introuvable. Rechargez la page ou choisissez une autre réunion.');
      return;
    }

    setModificationsStagiaires({
      ...modificationsStagiaires,
      ajouts: [...modificationsStagiaires.ajouts, {
        adherent: selectedAdherent,
        type: 'stagiaire',
        reunionElection: reunion,
      }]
    });

    setShowAjoutStagiaireModal(false);
    setSelectedAdherent(null);
    setSearchTerm('');
  };

  // Gestion du retrait
  const handleOpenRetrait = (membre) => {
    setRetraitForm({
      membreId: membre._id,
      raisonRetrait: '',
      raisonRetraitAutre: '',
      dateRetrait: new Date().toISOString().split('T')[0],
    });
    setShowRetraitModal(true);
  };

  const handleConfirmRetrait = () => {
    if (!retraitForm.raisonRetrait || (retraitForm.raisonRetrait === 'autre' && !retraitForm.raisonRetraitAutre)) {
      return;
    }

    const membre = composition.actifs.find(m => m._id === retraitForm.membreId);
    if (!membre) return;

    const retrait = {
      membreId: retraitForm.membreId,
      raisonRetrait: retraitForm.raisonRetrait,
      raisonRetraitAutre: retraitForm.raisonRetraitAutre,
      dateRetrait: retraitForm.dateRetrait,
    };

    if (membre.type === 'bureau') {
      setModificationsBureau({
        ...modificationsBureau,
        retraits: [...modificationsBureau.retraits, retrait]
      });
    } else if (membre.type === 'conseil') {
      setModificationsConseil({
        ...modificationsConseil,
        retraits: [...modificationsConseil.retraits, retrait]
      });
    } else if (membre.type === 'second_college') {
      setModificationsSecondCollege({
        ...modificationsSecondCollege,
        retraits: [...modificationsSecondCollege.retraits, retrait]
      });
    } else if (membre.type === 'stagiaire') {
      setModificationsStagiaires({
        ...modificationsStagiaires,
        retraits: [...modificationsStagiaires.retraits, retrait]
      });
    }

    setShowRetraitModal(false);
  };

  // Annuler un ajout
  const handleCancelAjout = (type, index) => {
    if (type === 'bureau') {
      const newAjouts = [...modificationsBureau.ajouts];
      newAjouts.splice(index, 1);
      setModificationsBureau({ ...modificationsBureau, ajouts: newAjouts });
    } else if (type === 'conseil') {
      const newAjouts = [...modificationsConseil.ajouts];
      newAjouts.splice(index, 1);
      setModificationsConseil({ ...modificationsConseil, ajouts: newAjouts });
    } else if (type === 'second_college') {
      const newAjouts = [...modificationsSecondCollege.ajouts];
      newAjouts.splice(index, 1);
      setModificationsSecondCollege({ ...modificationsSecondCollege, ajouts: newAjouts });
    } else if (type === 'stagiaire') {
      const newAjouts = [...modificationsStagiaires.ajouts];
      newAjouts.splice(index, 1);
      setModificationsStagiaires({ ...modificationsStagiaires, ajouts: newAjouts });
    }
  };

  // Annuler un retrait
  const handleCancelRetrait = (type, index) => {
    if (type === 'bureau') {
      const newRetraits = [...modificationsBureau.retraits];
      newRetraits.splice(index, 1);
      setModificationsBureau({ ...modificationsBureau, retraits: newRetraits });
    } else if (type === 'conseil') {
      const newRetraits = [...modificationsConseil.retraits];
      newRetraits.splice(index, 1);
      setModificationsConseil({ ...modificationsConseil, retraits: newRetraits });
    } else if (type === 'second_college') {
      const newRetraits = [...modificationsSecondCollege.retraits];
      newRetraits.splice(index, 1);
      setModificationsSecondCollege({ ...modificationsSecondCollege, retraits: newRetraits });
    } else if (type === 'stagiaire') {
      const newRetraits = [...modificationsStagiaires.retraits];
      newRetraits.splice(index, 1);
      setModificationsStagiaires({ ...modificationsStagiaires, retraits: newRetraits });
    }
  };

  // Compter le total de modifications
  const getTotalModifications = () => {
    return (
      modificationsBureau.ajouts.length + modificationsBureau.retraits.length +
      modificationsConseil.ajouts.length + modificationsConseil.retraits.length +
      modificationsSecondCollege.ajouts.length + modificationsSecondCollege.retraits.length +
      modificationsStagiaires.ajouts.length + modificationsStagiaires.retraits.length
    );
  };

  // Sauvegarde globale
  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      // Combiner toutes les modifications et transformer en format attendu par le backend
      const toutesLesModifications = {
        ajouts: [
          ...modificationsBureau.ajouts.map(a => ({
            adherentId: a.adherent._id,
            type: 'bureau',
            fonction: a.fonction,
            reunionElectionId: a.reunionElection?._id ?? a.reunionElection,
          })),
          ...modificationsConseil.ajouts.map(a => ({
            adherentId: a.adherent._id,
            type: 'conseil',
            fraction: a.fraction,
            ordre: a.ordre,
            reunionElectionId: a.reunionElection?._id ?? a.reunionElection,
            estMembreDeDroit: a.estMembreDeDroit,
          })),
          ...modificationsSecondCollege.ajouts.map((a) => ({
            secondCollegeHorsPlateforme: true,
            type: 'second_college',
            reunionElectionId: a.reunionElection?._id ?? a.reunionElection,
            estMembreDeDroitSecondCollege: a.estMembreDeDroitSecondCollege,
            typeMembreDeDroit: a.typeMembreDeDroit,
            numeroPosteSecondCollege: a.numeroPosteSecondCollege,
            labelPosteSecondCollege: a.labelPosteSecondCollege,
            structureSecondCollege: a.structureSecondCollege,
            nomRepresentantSecondCollege: a.nomRepresentantSecondCollege,
            emailRepresentantSecondCollege: a.emailRepresentantSecondCollege,
          })),
          ...modificationsStagiaires.ajouts.map(a => ({
            adherentId: a.adherent._id,
            type: 'stagiaire',
            reunionElectionId: a.reunionElection?._id ?? a.reunionElection,
          })),
        ],
        retraits: [
          ...modificationsBureau.retraits,
          ...modificationsConseil.retraits,
          ...modificationsSecondCollege.retraits,
          ...modificationsStagiaires.retraits,
        ],
        modifications: [],
        generatePdf,
      };

      const { data: resultats } = await axios.put(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/masse`,
        toutesLesModifications,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const erreurs = Array.isArray(resultats?.erreurs) ? resultats.erreurs : [];
      if (erreurs.length > 0) {
        const msg = erreurs
          .map((e) => (e && e.erreur != null ? String(e.erreur) : JSON.stringify(e)))
          .join('\n');
        alert(
          `Toutes les modifications n'ont pas pu être enregistrées :\n\n${msg}\n\n` +
            'Corrigez le problème (par ex. poste déjà occupé) puis réessayez.'
        );
        return;
      }

      if (generatePdf) {
        alert('Enregistrement effectué. Le PDF a été archivé dans « Documents de composition ».');
      } else if (getTotalModifications() > 0) {
        alert('Modifications enregistrées avec succès');
      }

      navigate('/admin/composition-conseil');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde des modifications');
    } finally {
      setSaving(false);
      setShowConfirmModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/composition-conseil')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour à la composition
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Modifier la composition - ADAR</h1>
              <p className="mt-2 text-gray-600">
                Gérez l'ensemble de la composition du conseil d'administration
              </p>
            </div>
            <button
              onClick={() => setShowConfirmModal(true)}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
            >
              {getTotalModifications() === 0
                ? 'Enregistrer'
                : `Enregistrer (${getTotalModifications()} modification${getTotalModifications() > 1 ? 's' : ''})`}
            </button>
          </div>
        </div>

        {/* Navigation par sections */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveSection('conseil')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeSection === 'conseil'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Conseil d'administration - Premier Collège (9 postes)
                {(modificationsConseil.ajouts.length + modificationsConseil.retraits.length) > 0 && (
                  <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-600 rounded-full">
                    {modificationsConseil.ajouts.length + modificationsConseil.retraits.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveSection('second_college')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeSection === 'second_college'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Conseil d'administration - Second Collège (5 postes)
                {(modificationsSecondCollege.ajouts.length + modificationsSecondCollege.retraits.length) > 0 && (
                  <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-600 rounded-full">
                    {modificationsSecondCollege.ajouts.length + modificationsSecondCollege.retraits.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveSection('bureau')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeSection === 'bureau'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Bureau (7 postes)
                {(modificationsBureau.ajouts.length + modificationsBureau.retraits.length) > 0 && (
                  <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-600 rounded-full">
                    {modificationsBureau.ajouts.length + modificationsBureau.retraits.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveSection('stagiaires')}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeSection === 'stagiaires'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Administrateurs Stagiaires
                {(modificationsStagiaires.ajouts.length + modificationsStagiaires.retraits.length) > 0 && (
                  <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-600 rounded-full">
                    {modificationsStagiaires.ajouts.length + modificationsStagiaires.retraits.length}
                  </span>
                )}
              </button>
            </nav>
          </div>
        </div>

        {/* Section Bureau */}
        {activeSection === 'bureau' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Bureau (7 postes)</h2>
              <p className="mt-1 text-sm text-gray-600">
                Président, 2 vice-présidents, secrétaire, secrétaire-adjoint, trésorier, trésorier-adjoint
              </p>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fonction</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {[
                      'president',
                      'vice_president',
                      'vice_president_2',
                      'secretaire',
                      'secretaire_adjoint',
                      'tresorier',
                      'tresorier_adjoint'
                    ].map((fonction) => {
                      const membre = membresBureau.find(m => m.fonction === fonction);
                      const ajoutEnCours = modificationsBureau.ajouts.find(a => a.fonction === fonction);
                      const retraitEnCours = modificationsBureau.retraits.find(r => r.membreId === membre?._id);
                      
                      return (
                        <tr key={fonction} className={!membre && !ajoutEnCours ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {getFonctionLabel(fonction)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <div className="flex items-center">
                                <span className="text-green-600 font-medium">
                                  {ajoutEnCours.adherent.prenom} {ajoutEnCours.adherent.nom.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                  Ajout en cours
                                </span>
                              </div>
                            ) : retraitEnCours ? (
                              <div className="flex items-center">
                                <span className="text-red-600 line-through">
                                  {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                  Retrait en cours
                                </span>
                              </div>
                            ) : membre ? (
                              <span className="text-gray-900">
                                {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">Vacant</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ajoutEnCours ? (
                              getReunionLabel(ajoutEnCours.reunionElection)
                            ) : membre?.reunionElection?.date ? (
                              formatReunionElectionLabel(membre.reunionElection, 'ADAR')
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <button
                                onClick={() => handleCancelAjout('bureau', modificationsBureau.ajouts.indexOf(ajoutEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler l'ajout
                              </button>
                            ) : retraitEnCours ? (
                              <button
                                onClick={() => handleCancelRetrait('bureau', modificationsBureau.retraits.indexOf(retraitEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler le retrait
                              </button>
                            ) : membre ? (
                              <button
                                onClick={() => handleOpenRetrait(membre)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Retirer
                              </button>
                            ) : (
                              <button
                                onClick={() => handleOpenAjoutBureau(fonction)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Ajouter
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Section Conseil */}
        {activeSection === 'conseil' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Conseil d'administration (9 postes)</h2>
              <p className="mt-1 text-sm text-gray-600">
                3 tiers de 3 administrateurs + membre de droit (SAR) + membres cooptés
              </p>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* 1er tiers */}
                    {[1, 2, 3].map((ordre) => {
                      const membre = membresConseil.find(m => m.fraction === '1er_tiers' && m.ordre === ordre && !m.estCoopte && !m.estMembreDeDroit);
                      const ajoutEnCours = modificationsConseil.ajouts.find(a => a.fraction === '1er_tiers' && a.ordre === ordre);
                      const retraitEnCours = modificationsConseil.retraits.find(r => r.membreId === membre?._id);
                      
                      return (
                        <tr key={`1er_tiers-${ordre}`} className={!membre && !ajoutEnCours ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            1er tiers / {ordre}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <div className="flex items-center">
                                <span className="text-green-600 font-medium">
                                  {ajoutEnCours.adherent.prenom} {ajoutEnCours.adherent.nom.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                  Ajout en cours
                                </span>
                              </div>
                            ) : retraitEnCours ? (
                              <div className="flex items-center">
                                <span className="text-red-600 line-through">
                                  {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                  Retrait en cours
                                </span>
                              </div>
                            ) : membre ? (
                              <span className="text-gray-900">
                                {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">Vacant</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getPeriodeLabel('1er_tiers')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ajoutEnCours ? (
                              getReunionLabel(ajoutEnCours.reunionElection)
                            ) : membre?.reunionElection?.date ? (
                              formatReunionElectionLabel(membre.reunionElection, 'ADAR')
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <button
                                onClick={() => handleCancelAjout('conseil', modificationsConseil.ajouts.indexOf(ajoutEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler l'ajout
                              </button>
                            ) : retraitEnCours ? (
                              <button
                                onClick={() => handleCancelRetrait('conseil', modificationsConseil.retraits.indexOf(retraitEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler le retrait
                              </button>
                            ) : membre ? (
                              <button
                                onClick={() => handleOpenRetrait(membre)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Retirer
                              </button>
                            ) : (
                              <button
                                onClick={() => handleOpenAjoutConseil('1er_tiers', ordre)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Ajouter
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    
                    {/* Séparateur */}
                    <tr className="bg-gray-100">
                      <td colSpan="5" className="px-6 py-2"></td>
                    </tr>
                    
                    {/* 2e tiers */}
                    {[1, 2, 3].map((ordre) => {
                      const membre = membresConseil.find(m => m.fraction === '2e_tiers' && m.ordre === ordre && !m.estCoopte && !m.estMembreDeDroit);
                      const ajoutEnCours = modificationsConseil.ajouts.find(a => a.fraction === '2e_tiers' && a.ordre === ordre);
                      const retraitEnCours = modificationsConseil.retraits.find(r => r.membreId === membre?._id);
                      
                      return (
                        <tr key={`2e_tiers-${ordre}`} className={!membre && !ajoutEnCours ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            2e tiers / {ordre}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <div className="flex items-center">
                                <span className="text-green-600 font-medium">
                                  {ajoutEnCours.adherent.prenom} {ajoutEnCours.adherent.nom.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                  Ajout en cours
                                </span>
                              </div>
                            ) : retraitEnCours ? (
                              <div className="flex items-center">
                                <span className="text-red-600 line-through">
                                  {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                  Retrait en cours
                                </span>
                              </div>
                            ) : membre ? (
                              <span className="text-gray-900">
                                {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">Vacant</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getPeriodeLabel('2e_tiers')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ajoutEnCours ? (
                              getReunionLabel(ajoutEnCours.reunionElection)
                            ) : membre?.reunionElection?.date ? (
                              formatReunionElectionLabel(membre.reunionElection, 'ADAR')
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <button
                                onClick={() => handleCancelAjout('conseil', modificationsConseil.ajouts.indexOf(ajoutEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler l'ajout
                              </button>
                            ) : retraitEnCours ? (
                              <button
                                onClick={() => handleCancelRetrait('conseil', modificationsConseil.retraits.indexOf(retraitEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler le retrait
                              </button>
                            ) : membre ? (
                              <button
                                onClick={() => handleOpenRetrait(membre)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Retirer
                              </button>
                            ) : (
                              <button
                                onClick={() => handleOpenAjoutConseil('2e_tiers', ordre)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Ajouter
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    
                    {/* Séparateur */}
                    <tr className="bg-gray-100">
                      <td colSpan="5" className="px-6 py-2"></td>
                    </tr>
                    
                    {/* 3e tiers */}
                    {[1, 2, 3].map((ordre) => {
                      const membre = membresConseil.find(m => m.fraction === '3e_tiers' && m.ordre === ordre && !m.estCoopte && !m.estMembreDeDroit);
                      const ajoutEnCours = modificationsConseil.ajouts.find(a => a.fraction === '3e_tiers' && a.ordre === ordre);
                      const retraitEnCours = modificationsConseil.retraits.find(r => r.membreId === membre?._id);
                      
                      return (
                        <tr key={`3e_tiers-${ordre}`} className={!membre && !ajoutEnCours ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            3e tiers / {ordre}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <div className="flex items-center">
                                <span className="text-green-600 font-medium">
                                  {ajoutEnCours.adherent.prenom} {ajoutEnCours.adherent.nom.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                  Ajout en cours
                                </span>
                              </div>
                            ) : retraitEnCours ? (
                              <div className="flex items-center">
                                <span className="text-red-600 line-through">
                                  {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                  Retrait en cours
                                </span>
                              </div>
                            ) : membre ? (
                              <span className="text-gray-900">
                                {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">Vacant</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getPeriodeLabel('3e_tiers')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ajoutEnCours ? (
                              getReunionLabel(ajoutEnCours.reunionElection)
                            ) : membre?.reunionElection?.date ? (
                              formatReunionElectionLabel(membre.reunionElection, 'ADAR')
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <button
                                onClick={() => handleCancelAjout('conseil', modificationsConseil.ajouts.indexOf(ajoutEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler l'ajout
                              </button>
                            ) : retraitEnCours ? (
                              <button
                                onClick={() => handleCancelRetrait('conseil', modificationsConseil.retraits.indexOf(retraitEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler le retrait
                              </button>
                            ) : membre ? (
                              <button
                                onClick={() => handleOpenRetrait(membre)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Retirer
                              </button>
                            ) : (
                              <button
                                onClick={() => handleOpenAjoutConseil('3e_tiers', ordre)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Ajouter
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    
                    {/* Membres cooptés */}
                    {membresConseil.filter(m => m.estCoopte).map((membre) => {
                      const retraitEnCours = modificationsConseil.retraits.find(r => r.membreId === membre._id);
                      
                      return (
                        <tr key={membre._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-700 font-medium">
                            Membre coopté
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {retraitEnCours ? (
                              <div className="flex items-center">
                                <span className="text-red-600 line-through">
                                  {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                  Retrait en cours
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-900">
                                {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {membre.dateCooptation ? new Date(membre.dateCooptation).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {retraitEnCours ? (
                              <button
                                onClick={() => handleCancelRetrait('conseil', modificationsConseil.retraits.indexOf(retraitEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler le retrait
                              </button>
                            ) : (
                              <button
                                onClick={() => handleOpenRetrait(membre)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Retirer
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Section Second Collège */}
        {activeSection === 'second_college' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Second Collège (5 postes)</h2>
              <p className="mt-1 text-sm text-gray-600">
                2 membres de droit + 3 autres membres
              </p>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Représentant</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Membres de droit */}
                    {[
                      { type: 'representant_cooperatives', label: 'Représentant des coopératives' },
                      { type: 'representant_syndicat', label: 'Représentant du syndicat' }
                    ].map((poste) => {
                      const membre = membresSecondCollege.find(m => m.estMembreDeDroitSecondCollege && m.typeMembreDeDroit === poste.type);
                      const ajoutEnCours = modificationsSecondCollege.ajouts.find(a => a.estMembreDeDroitSecondCollege && a.typeMembreDeDroit === poste.type);
                      const retraitEnCours = modificationsSecondCollege.retraits.find(r => r.membreId === membre?._id);
                      
                      return (
                        <tr key={poste.type} className={!membre && !ajoutEnCours ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-700 font-medium">
                            {poste.label}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <div className="flex flex-col">
                                <span className="text-green-600 font-medium">
                                  {ajoutEnCours.nomRepresentantSecondCollege} — {ajoutEnCours.structureSecondCollege}
                                </span>
                                <span className="text-xs text-gray-500">{ajoutEnCours.emailRepresentantSecondCollege}</span>
                                <span className="mt-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded w-fit">
                                  Ajout en cours
                                </span>
                              </div>
                            ) : retraitEnCours ? (
                              <div className="flex items-center">
                                <span className="text-red-600 line-through">
                                  {getSecondCollegeDisplay(membre).line1} {getSecondCollegeDisplay(membre).line2}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                  Retrait en cours
                                </span>
                              </div>
                            ) : membre ? (
                              <div>
                                <span className="text-gray-900 block">
                                  {getSecondCollegeDisplay(membre).line1} {getSecondCollegeDisplay(membre).line2}
                                </span>
                                {getSecondCollegeDisplay(membre).email && (
                                  <span className="text-xs text-gray-500">{getSecondCollegeDisplay(membre).email}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Vacant</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ajoutEnCours ? (
                              getReunionLabel(ajoutEnCours.reunionElection?._id || ajoutEnCours.reunionElection)
                            ) : membre?.reunionElection?.date ? (
                              formatReunionElectionLabel(membre.reunionElection, 'ADAR')
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <button
                                onClick={() => handleCancelAjout('second_college', modificationsSecondCollege.ajouts.indexOf(ajoutEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler l'ajout
                              </button>
                            ) : retraitEnCours ? (
                              <button
                                onClick={() => handleCancelRetrait('second_college', modificationsSecondCollege.retraits.indexOf(retraitEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler le retrait
                              </button>
                            ) : membre ? (
                              <button
                                onClick={() => handleOpenRetrait(membre)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Retirer
                              </button>
                            ) : (
                              <button
                                onClick={() => handleOpenAjoutSecondCollege(poste.type)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Ajouter
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    
                    {/* Séparateur */}
                    <tr className="bg-gray-100">
                      <td colSpan="5" className="px-6 py-2"></td>
                    </tr>
                    
                    {/* Autres membres (3 postes) */}
                    {[1, 2, 3].map((numero) => {
                      const membre = membresSecondCollege.find(m => !m.estMembreDeDroitSecondCollege && m.numeroPosteSecondCollege === numero);
                      const ajoutEnCours = modificationsSecondCollege.ajouts.find(a => !a.estMembreDeDroitSecondCollege && a.numeroPosteSecondCollege === numero);
                      const retraitEnCours = modificationsSecondCollege.retraits.find(r => r.membreId === membre?._id);
                      
                      return (
                        <tr key={`poste-${numero}`} className={!membre && !ajoutEnCours ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {ajoutEnCours?.labelPosteSecondCollege || membre?.labelPosteSecondCollege || `Poste ${numero}`}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <div className="flex flex-col">
                                <span className="text-green-600 font-medium">
                                  {ajoutEnCours.nomRepresentantSecondCollege} — {ajoutEnCours.structureSecondCollege}
                                </span>
                                <span className="text-xs text-gray-500">{ajoutEnCours.emailRepresentantSecondCollege}</span>
                                <span className="mt-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded w-fit">
                                  Ajout en cours
                                </span>
                              </div>
                            ) : retraitEnCours ? (
                              <div className="flex items-center">
                                <span className="text-red-600 line-through">
                                  {getSecondCollegeDisplay(membre).line1} {getSecondCollegeDisplay(membre).line2}
                                </span>
                                <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                  Retrait en cours
                                </span>
                              </div>
                            ) : membre ? (
                              <div>
                                <span className="text-gray-900 block">
                                  {getSecondCollegeDisplay(membre).line1} {getSecondCollegeDisplay(membre).line2}
                                </span>
                                {getSecondCollegeDisplay(membre).email && (
                                  <span className="text-xs text-gray-500">{getSecondCollegeDisplay(membre).email}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Vacant</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ajoutEnCours ? (
                              getReunionLabel(ajoutEnCours.reunionElection?._id || ajoutEnCours.reunionElection)
                            ) : membre?.reunionElection?.date ? (
                              formatReunionElectionLabel(membre.reunionElection, 'ADAR')
                            ) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {ajoutEnCours ? (
                              <button
                                onClick={() => handleCancelAjout('second_college', modificationsSecondCollege.ajouts.indexOf(ajoutEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler l'ajout
                              </button>
                            ) : retraitEnCours ? (
                              <button
                                onClick={() => handleCancelRetrait('second_college', modificationsSecondCollege.retraits.indexOf(retraitEnCours))}
                                className="text-red-600 hover:text-red-900"
                              >
                                Annuler le retrait
                              </button>
                            ) : membre ? (
                              <button
                                onClick={() => handleOpenRetrait(membre)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Retirer
                              </button>
                            ) : (
                              <button
                                onClick={() => handleOpenAjoutSecondCollege(null, numero)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Ajouter
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Section Stagiaires */}
        {activeSection === 'stagiaires' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Administrateurs Stagiaires</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Liste des administrateurs stagiaires
                </p>
              </div>
              <button
                onClick={handleOpenAjoutStagiaire}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <UserPlusIcon className="h-5 w-5 mr-2" />
                Ajouter un stagiaire
              </button>
            </div>
            <div className="p-6">
              {membresStagiaires.length === 0 && modificationsStagiaires.ajouts.length === 0 ? (
                <div className="text-center py-12">
                  <UserPlusIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun administrateur stagiaire</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Commencez par ajouter un administrateur stagiaire
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adhérent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Stagiaires existants */}
                      {membresStagiaires.map((membre) => {
                        const retraitEnCours = modificationsStagiaires.retraits.find(r => r.membreId === membre._id);
                        
                        return (
                          <tr key={membre._id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {retraitEnCours ? (
                                <div className="flex items-center">
                                  <span className="text-red-600 line-through">
                                    {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                                  </span>
                                  <span className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                    Retrait en cours
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-900">
                                  {membre.adherent?.prenom} {membre.adherent?.nom?.toUpperCase()}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {membre.reunionElection?.date ? (
                                formatReunionElectionLabel(membre.reunionElection, 'ADAR')
                              ) : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {retraitEnCours ? (
                                <button
                                  onClick={() => handleCancelRetrait('stagiaire', modificationsStagiaires.retraits.indexOf(retraitEnCours))}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Annuler le retrait
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleOpenRetrait(membre)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Retirer
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      
                      {/* Ajouts en cours */}
                      {modificationsStagiaires.ajouts.map((ajout, index) => (
                        <tr key={`ajout-${index}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center">
                              <span className="text-green-600 font-medium">
                                {ajout.adherent.prenom} {ajout.adherent.nom.toUpperCase()}
                              </span>
                              <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                                Ajout en cours
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getReunionLabel(ajout.reunionElection)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleCancelAjout('stagiaire', index)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Annuler l'ajout
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Ajout Bureau */}
        {showAjoutBureauModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ajouter un membre au Bureau - {getFonctionLabel(ajoutForm.fonction)}
                </h3>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rechercher un adhérent *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Nom ou prénom..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  {searchTerm && filteredAdherents.length > 0 && (
                    <div className="mt-2 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                      {filteredAdherents.map((adherent) => (
                        <button
                          key={adherent._id}
                          onClick={() => {
                            setSelectedAdherent(adherent);
                            setSearchTerm('');
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {adherent.prenom} {adherent.nom}
                          </div>
                          <div className="text-sm text-gray-500">
                            {adherent.email}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedAdherent && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-medium text-green-900">
                          {selectedAdherent.prenom} {selectedAdherent.nom}
                        </div>
                        <div className="text-sm text-green-700">
                          {selectedAdherent.email}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedAdherent(null)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Réunion d'élection *
                  </label>
                  <select
                    value={ajoutForm.reunionElection}
                    onChange={(e) => setAjoutForm({ ...ajoutForm, reunionElection: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Sélectionner une réunion</option>
                    {reunions.map((reunion) => (
                      <option key={reunion._id} value={reunion._id}>
                        {getReunionTypeShort(reunion.type, 'ADAR')} - {new Date(reunion.date).toLocaleDateString('fr-FR')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAjoutBureauModal(false);
                    setSelectedAdherent(null);
                    setSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmAjoutBureau}
                  disabled={!selectedAdherent || !ajoutForm.reunionElection}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Ajout Conseil */}
        {showAjoutConseilModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ajouter un membre au Conseil
                </h3>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rechercher un adhérent *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Nom ou prénom..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  {searchTerm && filteredAdherents.length > 0 && (
                    <div className="mt-2 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                      {filteredAdherents.map((adherent) => (
                        <button
                          key={adherent._id}
                          onClick={() => {
                            setSelectedAdherent(adherent);
                            setSearchTerm('');
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {adherent.prenom} {adherent.nom}
                          </div>
                          <div className="text-sm text-gray-500">
                            {adherent.email}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedAdherent && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-medium text-green-900">
                          {selectedAdherent.prenom} {selectedAdherent.nom}
                        </div>
                        <div className="text-sm text-green-700">
                          {selectedAdherent.email}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedAdherent(null)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Réunion d'élection *
                  </label>
                  <select
                    value={ajoutForm.reunionElection}
                    onChange={(e) => setAjoutForm({ ...ajoutForm, reunionElection: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Sélectionner une réunion</option>
                    {reunions.map((reunion) => (
                      <option key={reunion._id} value={reunion._id}>
                        {getReunionTypeShort(reunion.type, 'ADAR')} - {new Date(reunion.date).toLocaleDateString('fr-FR')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAjoutConseilModal(false);
                    setSelectedAdherent(null);
                    setSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmAjoutConseil}
                  disabled={!selectedAdherent || !ajoutForm.reunionElection}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Ajout Second Collège — représentation (pas de compte adhérent) */}
        {showAjoutSecondCollegeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {ajoutForm.estMembreDeDroitSecondCollege
                    ? 'Renseigner le second collège (membre de droit)'
                    : 'Renseigner le second collège (poste numéroté)'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Le représentant n&apos;a pas besoin d&apos;être inscrit sur la plateforme.
                </p>
              </div>

              <div className="p-6 space-y-4">
                {ajoutForm.estMembreDeDroitSecondCollege ? (
                  <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-sm text-indigo-900">
                    {ajoutForm.typeMembreDeDroit === 'representant_cooperatives' && 'Représentant des coopératives'}
                    {ajoutForm.typeMembreDeDroit === 'representant_syndicat' && 'Représentant du syndicat'}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du poste (libellé) *
                    </label>
                    <input
                      type="text"
                      value={ajoutForm.labelPosteSecondCollege}
                      onChange={(e) => setAjoutForm({ ...ajoutForm, labelPosteSecondCollege: e.target.value })}
                      placeholder="Ex. : représentation professionnelle, GDSA…"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Affiché à la place de « Poste 1 », « Poste 2 » ou « Poste 3 ».
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Structure / organisation représentée *
                  </label>
                  <input
                    type="text"
                    value={ajoutForm.structureSecondCollege}
                    onChange={(e) => setAjoutForm({ ...ajoutForm, structureSecondCollege: e.target.value })}
                    placeholder="Nom de la structure"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom du représentant *
                  </label>
                  <input
                    type="text"
                    value={ajoutForm.nomRepresentantSecondCollege}
                    onChange={(e) => setAjoutForm({ ...ajoutForm, nomRepresentantSecondCollege: e.target.value })}
                    placeholder="Prénom et nom"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail du représentant *
                  </label>
                  <input
                    type="email"
                    value={ajoutForm.emailRepresentantSecondCollege}
                    onChange={(e) => setAjoutForm({ ...ajoutForm, emailRepresentantSecondCollege: e.target.value })}
                    placeholder="courriel@exemple.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Réunion d&apos;élection (acte) *
                  </label>
                  <select
                    value={ajoutForm.reunionElection}
                    onChange={(e) => setAjoutForm({ ...ajoutForm, reunionElection: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Sélectionner une réunion</option>
                    {reunions.map((reunion) => (
                      <option key={reunion._id} value={reunion._id}>
                        {getReunionTypeShort(reunion.type, 'ADAR')} - {new Date(reunion.date).toLocaleDateString('fr-FR')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAjoutSecondCollegeModal(false);
                    setSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAjoutSecondCollege}
                  disabled={!ajoutForm.reunionElection}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Ajout Stagiaire */}
        {showAjoutStagiaireModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ajouter un administrateur stagiaire
                </h3>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rechercher un adhérent *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Nom ou prénom..."
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  {searchTerm && filteredAdherents.length > 0 && (
                    <div className="mt-2 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                      {filteredAdherents.map((adherent) => (
                        <button
                          key={adherent._id}
                          onClick={() => {
                            setSelectedAdherent(adherent);
                            setSearchTerm('');
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {adherent.prenom} {adherent.nom}
                          </div>
                          <div className="text-sm text-gray-500">
                            {adherent.email}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedAdherent && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="font-medium text-green-900">
                          {selectedAdherent.prenom} {selectedAdherent.nom}
                        </div>
                        <div className="text-sm text-green-700">
                          {selectedAdherent.email}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedAdherent(null)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Réunion d'élection *
                  </label>
                  <select
                    value={ajoutForm.reunionElection}
                    onChange={(e) => setAjoutForm({ ...ajoutForm, reunionElection: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Sélectionner une réunion</option>
                    {reunions.map((reunion) => (
                      <option key={reunion._id} value={reunion._id}>
                        {getReunionTypeShort(reunion.type, 'ADAR')} - {new Date(reunion.date).toLocaleDateString('fr-FR')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowAjoutStagiaireModal(false);
                    setSelectedAdherent(null);
                    setSearchTerm('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmAjoutStagiaire}
                  disabled={!selectedAdherent || !ajoutForm.reunionElection}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Retrait */}
        {showRetraitModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Retirer un membre
                </h3>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Raison du retrait *
                  </label>
                  <select
                    value={retraitForm.raisonRetrait}
                    onChange={(e) => setRetraitForm({ ...retraitForm, raisonRetrait: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  >
                    <option value="">Sélectionner une raison</option>
                    <option value="demission">Démission</option>
                    <option value="non_adhesion">Non-adhésion</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                {retraitForm.raisonRetrait === 'autre' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Préciser la raison *
                    </label>
                    <input
                      type="text"
                      value={retraitForm.raisonRetraitAutre}
                      onChange={(e) => setRetraitForm({ ...retraitForm, raisonRetraitAutre: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de retrait
                  </label>
                  <input
                    type="date"
                    value={retraitForm.dateRetrait}
                    onChange={(e) => setRetraitForm({ ...retraitForm, dateRetrait: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowRetraitModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmRetrait}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Confirmer le retrait
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Confirmation */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Composition finale
              </h3>
              
              <p className="text-gray-600 mb-4">
                {getTotalModifications() === 0
                  ? 'Aucune modification en cours. Vous pouvez archiver la composition actuelle en PDF.'
                  : 'Vous êtes sur le point d\'enregistrer les modifications de la composition du conseil.'}
              </p>

              <div className="mb-6">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generatePdf}
                    onChange={(e) => setGeneratePdf(e.target.checked)}
                    className="h-5 w-5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Enregistrer la composition en PDF
                  </span>
                </label>
                <p className="ml-8 mt-1 text-xs text-gray-500">
                  Le PDF sera disponible dans la section "Documents de composition"
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setGeneratePdf(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? 'Enregistrement...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
